import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-creation_date' };

const createState = (options = {}) => ({
    shipmentitems: [],
    shipmentitems_url: '/api/shipmentitems/',
    shipmentitems_filters: DEFAULT_FILTERS,
    shipmentitems_count: 0,
    shipmentitems_loaded: false,
    shipmentitems_loading: false,
    shipmentitems_loading_errors: null,

    shipmentitems_stats: {},
    shipmentitems_stats_loaded: false,
    shipmentitems_stats_loading: false,
    shipmentitems_stats_loading_errors: null,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    _storageOptions: options || {},
});

const mutations = {
    updateShipmentItems(state, shipmentitems) {
        state.shipmentitems = shipmentitems;
        state.shipmentitems_loaded = true;
        state.shipmentitems_loading = false;
        state.shipmentitems_loading_errors = null;
    },
    updateShipmentItemsUrl(state, shipmentitems_url) {
        state.shipmentitems_url = shipmentitems_url;
    },
    updateShipmentItemsCount(state, shipmentitems_count) {
        state.shipmentitems_count = shipmentitems_count;
    },
    updateShipmentItemsFilters(state, shipmentitems_filters) {
        state.shipmentitems_filters = shipmentitems_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.shipmentitems_filters, fieldsToSave);
        }
    },
    updateShipmentItemsLoading(state, shipmentitems_loading) {
        state.shipmentitems_loading = shipmentitems_loading;
    },
    updateShipmentItemsLoadingErrors(state, shipmentitems_loading_errors) {
        state.shipmentitems_loading_errors = shipmentitems_loading_errors;
        state.shipmentitems_loading = false;
    },

    updateShipmentItemsStats(state, shipmentitems_stats) {
        state.shipmentitems_stats = shipmentitems_stats;
        state.shipmentitems_stats_loaded = true;
    },
    updateShipmentItemsStatsLoading(state, loading) {
        state.shipmentitems_stats_loading = loading;
    },
    updateShipmentItemsStatsLoadingErrors(state, errors) {
        state.shipmentitems_stats_loading_errors = errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },

};

const actions = {

    async fetchShipmentItems({ commit, dispatch, state }, params) {
        if (!resolvePermissionCheck(store.getters['session/current_user_permissions'], "core.view_shipmentitem")) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateShipmentItemsLoading', true);
        commit('updateShipmentItemsLoadingErrors', null);

        try {
            const response = await axios.get(state.shipmentitems_url, {
                params: state.shipmentitems_filters,
                cancelToken: source.token,
            });
            commit('updateShipmentItemsCount', response.data.count);
            commit('updateShipmentItems', response.data.results);
            dispatch('fetchShipmentItemsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateShipmentItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateShipmentItemsLoading', false);
        }
    },

    async fetchShipmentItemsStats({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.view_allocation") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateShipmentItemsStatsLoading', true);
        commit('updateShipmentItemsStatsLoadingErrors', null);

        try {
            const response = await axios.get('/api/shipmentitems/stats/', {
                params: state.shipmentitems_filters,
                cancelToken: source.token,
            });
            commit('updateShipmentItemsStatsLoading', false);
            commit('updateShipmentItemsStats', response.data);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateShipmentItemsStatsLoadingErrors', error.details);
            commit('updateShipmentItemsStatsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit('updateShipmentItems', []);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateShipmentItemsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
        
        if (params && params.url) {
            await commit('updateShipmentItemsUrl', params.url);
        }
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});